<script setup lang="ts">
/**
 * Line chart over categories (months, days, stages): one line per series, a
 * gap where a series has no value. A single series gets a soft area wash
 * under its line; several series stay lines only, so they never cover each
 * other.
 *
 * A crosshair follows the pointer and snaps to the nearest category, the
 * tooltip lists every series there — the reader aims at a date, never at a
 * 2px line. With focus, the arrow keys walk the categories. Two or more series
 * get a legend; hovering an entry brings that line forward. Screen readers
 * get the values as a hidden table.
 *
 * The chart fills its box (give it a height) and draws at the real pixel size.
 */
import { computed, inject, ref, useTemplateRef } from 'vue';
import { i18nInjectionKey } from '../internal';
import { valueAxis } from './axis';
import { plotFrame, thinLabels } from './frame';
import { areaPath, linePath, type NxPoint } from './geometry';
import { announceTooltip } from './announce';
import NxChartLegend from './NxChartLegend.vue';
import NxChartLive from './NxChartLive.vue';
import NxChartTable from './NxChartTable.vue';
import NxChartTooltip, { type NxChartTooltipRow } from './NxChartTooltip.vue';
import {
  chartColor,
  chartValue,
  seriesExtent,
  type NxChartKey,
  type NxChartSeries,
} from './series';
import { useChartSize } from './useChartSize';

const props = withDefaults(
  defineProps<{
    categories: readonly string[];
    series: readonly NxChartSeries[];
    /** Accessible name of the chart („Offene Posten je Woche“). */
    ariaLabel?: string;
    /** `smooth` bends the line through the points without overshooting them; `linear` draws straight segments. */
    curve?: 'smooth' | 'linear';
    /** Area wash under the line: `auto` for a single series, `none` never. */
    area?: 'auto' | 'none';
    legend?: 'auto' | 'none';
    formatValue?: (value: number) => string;
    formatTick?: (value: number) => string;
    locale?: string;
    /** Spoken hint for keyboard users, appended to the plot's name. */
    keyboardHint?: string;
  }>(),
  {
    ariaLabel: undefined,
    curve: 'smooth',
    area: 'auto',
    legend: 'auto',
    formatValue: undefined,
    formatTick: undefined,
    locale: undefined,
    keyboardHint: 'Pfeiltasten wechseln die Kategorie',
  }
);

const i18n = inject(i18nInjectionKey, null);
const locale = computed(() => props.locale ?? i18n?.locale?.value);
const numberFormat = computed(() => new Intl.NumberFormat(locale.value));
function format(value: number): string {
  return props.formatValue ? props.formatValue(value) : numberFormat.value.format(value);
}

const plotRef = useTemplateRef<HTMLElement>('plot');
const { width, height } = useChartSize(plotRef, { width: 480, height: 220 });

const uid = `nx-line-${Math.random().toString(36).slice(2, 9)}`;

const axis = computed(() => {
  const ext = seriesExtent(props.series);
  return valueAxis(ext.min, ext.max);
});
const frame = computed(() =>
  plotFrame(axis.value, width.value, height.value, {
    locale: locale.value,
    formatTick: props.formatTick,
    padRight: 12,
  })
);
/** Inset so the first and last point (and their markers) sit inside the plot. */
const INSET = 8;
const count = computed(() => props.categories.length);
function xAt(i: number): number {
  const fr = frame.value;
  if (count.value <= 1) return fr.left + fr.width / 2;
  return fr.left + INSET + (i / (count.value - 1)) * (fr.width - INSET * 2);
}
const step = computed(() =>
  count.value <= 1 ? frame.value.width : (frame.value.width - INSET * 2) / (count.value - 1)
);

interface DrawnLine {
  key: NxChartKey;
  index: number;
  color: string;
  /** Runs between gaps. */
  runs: NxPoint[][];
  last: NxPoint | null;
}

const lines = computed<DrawnLine[]>(() => {
  const fr = frame.value;
  return props.series.map((s, si) => {
    const runs: NxPoint[][] = [];
    let run: NxPoint[] = [];
    let last: NxPoint | null = null;
    props.categories.forEach((_, i) => {
      const v = chartValue(s.values[i]);
      if (v === null) {
        if (run.length) runs.push(run);
        run = [];
        return;
      }
      const p = { x: xAt(i), y: fr.y(v) };
      run.push(p);
      last = p;
    });
    if (run.length) runs.push(run);
    return { key: s.key, index: si, color: chartColor(s.color, si), runs, last };
  });
});

const smooth = computed(() => props.curve === 'smooth');
const showArea = computed(() => props.area === 'auto' && props.series.length === 1);
const baseY = computed(() => frame.value.y(Math.max(axis.value.min, Math.min(0, axis.value.max))));

const xLabels = computed(() =>
  thinLabels(props.categories, step.value).map((l) => ({ ...l, x: xAt(l.index) }))
);

// ── Hover & keyboard ─────────────────────────────────────────────────────────
const active = ref<number | null>(null);
const highlightedSeries = ref<NxChartKey | null>(null);

function onPointer(event: PointerEvent) {
  const box = plotRef.value?.getBoundingClientRect();
  if (!box || !count.value) return;
  const x = event.clientX - box.left;
  const fr = frame.value;
  if (x < fr.left - INSET || x > fr.right + INSET) {
    active.value = null;
    return;
  }
  const i = count.value <= 1 ? 0 : Math.round((x - fr.left - INSET) / step.value);
  active.value = Math.max(0, Math.min(count.value - 1, i));
}

function onKeydown(event: KeyboardEvent) {
  const n = count.value;
  if (!n) return;
  const cur = active.value;
  let next: number;
  switch (event.key) {
    case 'ArrowRight':
    case 'ArrowDown':
      next = cur === null ? 0 : Math.min(cur + 1, n - 1);
      break;
    case 'ArrowLeft':
    case 'ArrowUp':
      next = cur === null ? n - 1 : Math.max(cur - 1, 0);
      break;
    case 'Home':
      next = 0;
      break;
    case 'End':
      next = n - 1;
      break;
    case 'Escape':
      active.value = null;
      return;
    default:
      return;
  }
  event.preventDefault();
  active.value = next;
}

/** Markers of the active category: one per series that has a value there. */
const activeDots = computed(() => {
  const i = active.value;
  if (i === null) return [];
  const fr = frame.value;
  return props.series.flatMap((s, si) => {
    const v = chartValue(s.values[i]);
    if (v === null) return [];
    return [{ key: s.key, x: xAt(i), y: fr.y(v), color: chartColor(s.color, si) }];
  });
});

const tooltip = computed(() => {
  const i = active.value;
  if (i === null) return null;
  const fr = frame.value;
  const rows: NxChartTooltipRow[] = props.series
    .map((s, si) => {
      const v = chartValue(s.values[i]);
      return {
        key: s.key,
        label: s.label,
        value: v === null ? '–' : format(v),
        color: chartColor(s.color, si),
        shape: 'line' as const,
        muted: v === null,
        order: v ?? -Infinity,
      };
    })
    // Highest first, so the rows read in the order the lines stand at this point.
    .sort((a, b) => b.order - a.order);
  const ys = activeDots.value.map((d) => d.y);
  const y = ys.length ? Math.min(...ys) : fr.top + fr.height / 2;
  return {
    x: xAt(i),
    y: Math.max(fr.top + 16, Math.min(y, fr.bottom - 16)),
    title: props.categories[i] ?? '',
    rows: props.series.length === 1 ? rows.map((r) => ({ ...r, color: undefined })) : rows,
  };
});

/** What the live region says: only while the plot has keyboard focus. */
const focused = ref(false);
const announcement = computed(() => (focused.value ? announceTooltip(tooltip.value) : ''));
const plotLabel = computed(() => [props.ariaLabel, props.keyboardHint].filter(Boolean).join('. '));

const legendItems = computed(() =>
  props.series.map((s, i) => ({ key: s.key, label: s.label, color: chartColor(s.color, i) }))
);
const showLegend = computed(() => props.legend === 'auto' && props.series.length > 1);

const table = computed(() => ({
  columns: ['', ...props.series.map((s) => s.label)],
  rows: props.categories.map((c, i) => ({
    key: i,
    cells: [
      c,
      ...props.series.map((s) => {
        const v = chartValue(s.values[i]);
        return v === null ? '–' : format(v);
      }),
    ],
  })),
}));

function lineClass(line: DrawnLine) {
  return {
    'is-dimmed': highlightedSeries.value !== null && highlightedSeries.value !== line.key,
    'is-highlighted': highlightedSeries.value === line.key,
  };
}
</script>

<template>
  <div class="nx-line-chart" role="group" :aria-label="props.ariaLabel">
    <div
      ref="plot"
      class="nx-line-chart__plot"
      tabindex="0"
      role="group"
      :aria-label="plotLabel"
      @pointermove="onPointer"
      @pointerleave="active = null"
      @keydown="onKeydown"
      @focus="focused = true"
      @blur="
        focused = false;
        active = null;
      "
    >
      <svg
        class="nx-line-chart__svg"
        :width="width"
        :height="height"
        :viewBox="`0 0 ${width} ${height}`"
        aria-hidden="true"
      >
        <defs>
          <linearGradient
            v-for="line in lines"
            :id="`${uid}-${line.index}`"
            :key="line.key"
            x1="0"
            x2="0"
            y1="0"
            y2="1"
          >
            <stop offset="0%" :stop-color="line.color" stop-opacity="0.22" />
            <stop offset="100%" :stop-color="line.color" stop-opacity="0" />
          </linearGradient>
        </defs>
        <g class="nx-line-chart__grid">
          <line
            v-for="tick in frame.tickLabels"
            :key="tick.value"
            :x1="frame.left"
            :x2="frame.right"
            :y1="tick.y"
            :y2="tick.y"
            :class="{ 'is-zero': tick.value === 0 }"
          />
        </g>
        <g class="nx-line-chart__ticks">
          <text
            v-for="tick in frame.tickLabels"
            :key="tick.value"
            :x="frame.left - 8"
            :y="tick.y"
            text-anchor="end"
            dominant-baseline="middle"
          >
            {{ tick.text }}
          </text>
        </g>
        <line
          v-if="active !== null"
          class="nx-line-chart__crosshair"
          :x1="xAt(active)"
          :x2="xAt(active)"
          :y1="frame.top"
          :y2="frame.bottom"
        />
        <g v-if="showArea" class="nx-line-chart__areas">
          <template v-for="line in lines" :key="line.key">
            <path
              v-for="(run, ri) in line.runs"
              :key="ri"
              :d="areaPath(run, baseY, smooth)"
              :fill="`url(#${uid}-${line.index})`"
            />
          </template>
        </g>
        <g class="nx-line-chart__lines">
          <g
            v-for="line in lines"
            :key="line.key"
            :class="lineClass(line)"
            class="nx-line-chart__series"
          >
            <path
              v-for="(run, ri) in line.runs"
              :key="ri"
              class="nx-line-chart__line"
              :d="linePath(run, smooth)"
              :style="{ stroke: line.color }"
              pathLength="1"
            />
            <!-- A lone point between gaps has no line to show it: draw its marker. -->
            <template v-for="(run, ri) in line.runs" :key="`s${ri}`">
              <circle
                v-if="run.length === 1"
                class="nx-line-chart__dot"
                :cx="run[0]!.x"
                :cy="run[0]!.y"
                r="3"
                :style="{ fill: line.color }"
              />
            </template>
            <circle
              v-if="line.last && active === null"
              class="nx-line-chart__end"
              :cx="line.last.x"
              :cy="line.last.y"
              r="4"
              :style="{ fill: line.color }"
            />
          </g>
        </g>
        <g class="nx-line-chart__active">
          <circle
            v-for="dot in activeDots"
            :key="dot.key"
            :cx="dot.x"
            :cy="dot.y"
            r="5"
            :style="{ fill: dot.color }"
          />
        </g>
        <g class="nx-line-chart__xlabels">
          <text
            v-for="label in xLabels"
            :key="label.index"
            :x="label.x"
            :y="height - 6"
            text-anchor="middle"
            :class="{ 'is-active': active === label.index }"
          >
            {{ label.text }}
          </text>
        </g>
      </svg>
      <NxChartTooltip
        v-if="tooltip"
        :x="tooltip.x"
        :y="tooltip.y"
        :box-width="width"
        :title="tooltip.title"
        :rows="tooltip.rows"
      />
      <NxChartLive :text="announcement" />
    </div>
    <NxChartLegend
      v-if="showLegend"
      v-model:highlighted="highlightedSeries"
      shape="line"
      :items="legendItems"
    />
    <NxChartTable :caption="props.ariaLabel" :columns="table.columns" :rows="table.rows" />
  </div>
</template>

<style scoped>
.nx-line-chart {
  position: relative;
  display: flex;
  flex-direction: column;
  gap: var(--nxt-space-sm);
  width: 100%;
  height: 100%;
  min-height: calc(var(--nxt-size-unit) * 8);
  min-width: 0;
}
.nx-line-chart__plot {
  position: relative;
  flex: 1 1 auto;
  min-height: 0;
  min-width: 0;
  border-radius: var(--nxt-radius-sm);
  outline: none;
}
.nx-line-chart__plot:focus-visible {
  box-shadow: 0 0 0 calc(var(--nxt-hairline) * 2) var(--nxt-focus-ring);
}
.nx-line-chart__svg {
  position: absolute;
  inset: 0;
  display: block;
  overflow: visible;
}
.nx-line-chart__grid line {
  stroke: var(--nxt-chart-grid);
  stroke-width: 1;
  shape-rendering: crispEdges;
}
.nx-line-chart__grid line.is-zero {
  stroke: var(--nxt-chart-baseline);
}
.nx-line-chart__ticks text,
.nx-line-chart__xlabels text {
  fill: var(--nxt-fg-muted);
  font-size: var(--nxt-font-size-xs);
  font-variant-numeric: tabular-nums;
}
.nx-line-chart__xlabels text.is-active {
  fill: var(--nxt-fg);
  font-weight: var(--nxt-font-weight-medium);
}
.nx-line-chart__crosshair {
  stroke: var(--nxt-chart-baseline);
  stroke-width: 1;
  shape-rendering: crispEdges;
}
.nx-line-chart__areas path {
  animation: nx-line-fade var(--nxt-motion-slow) var(--nxt-ease-out) both;
}
.nx-line-chart__line {
  fill: none;
  stroke-width: 2;
  stroke-linecap: round;
  stroke-linejoin: round;
  stroke-dasharray: 1;
  animation: nx-line-draw calc(var(--nxt-motion-slow) * 2.5) var(--nxt-ease-out) both;
}
.nx-line-chart__series {
  transition: opacity var(--nxt-motion-fast) var(--nxt-ease-out);
}
.nx-line-chart__series.is-dimmed {
  opacity: 0.25;
}
.nx-line-chart__series.is-highlighted .nx-line-chart__line {
  stroke-width: 2.5;
}
.nx-line-chart__end,
.nx-line-chart__dot,
.nx-line-chart__active circle {
  stroke: var(--nxt-surface-bg);
  stroke-width: 2;
}
.nx-line-chart__end {
  animation: nx-line-fade var(--nxt-motion-slow) var(--nxt-ease-out) both;
  animation-delay: calc(var(--nxt-motion-slow) * 1.5);
}
@keyframes nx-line-draw {
  from {
    stroke-dashoffset: 1;
  }
  to {
    stroke-dashoffset: 0;
  }
}
@keyframes nx-line-fade {
  from {
    opacity: 0;
  }
}
@media (prefers-reduced-motion: reduce) {
  .nx-line-chart__line,
  .nx-line-chart__areas path,
  .nx-line-chart__end {
    animation: none;
  }
  .nx-line-chart__series {
    transition: none;
  }
}
@media (forced-colors: active) {
  .nx-line-chart__line {
    stroke: CanvasText !important;
  }
}
</style>
